import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, ViewChild} from '@angular/core';
import {combineLatest, fromEvent, interval, Subscription} from 'rxjs';
import {startWith, throttle} from 'rxjs/operators';
import {Font, Fonts} from '../font-list';

@Component({
  selector: 'pl-typed-signature-pad',
  templateUrl: 'typed-signature-pad.component.html',
  styleUrls: ['./typed-signature-pad.component.scss']
})
export class TypedSignaturePadComponent implements AfterViewInit, OnDestroy {
  cdr = inject(ChangeDetectorRef);

  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('input') inputRef: ElementRef<HTMLDivElement>;

  font: Font;
  fontSize = 100;
  maxFontSize = 100;
  devicePixelRatio = 1;
  viewportScale = 1;

  @Input() signatureText = '';

  private readonly sizeThreshhold = 40;

  private fitTextIterations = 0;
  private readonly maxFitTextIterations = 300;

  get fontFamily() {
    return '"' + this.font?.name + '", sans-serif';
  }

  private subs = new Subscription();

  async ngAfterViewInit() {
    this.subs.add(combineLatest([
      fromEvent(window, 'resize').pipe(startWith(null)),
      fromEvent(window, 'orientationchange').pipe(startWith(null))
    ])
      .pipe(throttle(ev => interval(100), {leading: true, trailing: true}))
      .subscribe(e => {
        this.drawSignature();
      }));

    await this.selectFont(Fonts[0].id);

    setTimeout(() => this.drawSignature(), 100);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  drawSignature() {
    const canvas = this.canvasRef.nativeElement;
    this.devicePixelRatio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * this.devicePixelRatio;
    canvas.height = canvas.offsetHeight * this.devicePixelRatio;

    this.maxFontSize *= this.devicePixelRatio;

    const input = this.inputRef.nativeElement;
    this.viewportScale = Math.max(Math.min(input.offsetHeight/canvas.offsetHeight, 1), 0.5);

    this.fitTextIterations = 0;
    this.fitText();
    this.fitTextVertically();
  }

  // Returns signature image as data URL (see https://mdn.io/todataurl for the list of possible paramters)
  public toDataURL(imageType?: string, quality?: number): string {
    return this.canvasRef.nativeElement.toDataURL(imageType, quality);
  }

  public valid() {
    return this.signatureText.length > 0;
  }

  public clear() {
    this.signatureText = '';
    this.drawSignature();
    this.cdr.markForCheck();
  }

  private primeContext(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) {
    context.clearRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = 'black';
    context.font = `${this.fontSize * this.font?.baseSizeMultiplier}px "${this.font?.name}"`;
    context.textAlign = 'center';
    context.textBaseline = 'bottom';
  }

  private fitText() {
    this.fitTextIterations += 1;

    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');
    const allowedWidth = canvas.width - 60;

    this.primeContext(canvas, context);

    const measuredText = context.measureText(this.signatureText);
    if (this.fitTextIterations < this.maxFitTextIterations
      && measuredText.width > (allowedWidth + this.sizeThreshhold)
      && this.fontSize >= 10) {
      this.fontSize -= 1;
      this.fitText();
    } else if (this.fitTextIterations < this.maxFitTextIterations
      && measuredText.width < (allowedWidth - this.sizeThreshhold)
      && this.fontSize <= this.maxFontSize) {
      this.fontSize += 1;
      this.fitText();
    }

    context.fillText(this.signatureText, canvas.width / 2, canvas.height - (40 * this.devicePixelRatio));
  }

  async selectFont(fontId: string) {
    this.font = Fonts.find(f => f.id === fontId);
    this.cdr.detectChanges();

    this.drawSignature();
  }

  private fitTextVertically() {
    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');

    let measuredText = context.measureText(this.signatureText);
    do {
      this.fontSize -= 1;
      this.primeContext(canvas, context);

      measuredText = context.measureText(this.signatureText);
      context.fillText(this.signatureText, canvas.width / 2, canvas.height - (40 * this.devicePixelRatio));
    } while (measuredText.actualBoundingBoxAscent > (canvas.height - (40 * this.devicePixelRatio)) && this.fontSize >= 10 && this.fontSize <= this.maxFontSize);
  }

  focus() {
    const element = this.inputRef.nativeElement;
    element.focus();

    window.getSelection().selectAllChildren(element);
    window.getSelection().collapseToEnd();
  }
}
